import {
  isSessionArchived,
  metadataToMergedFrom,
  metadataToMergedInto,
  metadataToMergeRecord,
} from '../../../utils/sessionMergeState';
import {
  buildIncidentUrl,
  caseIdToLinkError,
  isValidUrl,
  normalizeCaseId,
  parseCaseIdFromUrl,
} from '../../../utils/austin';

export interface SessionMergeBannerLink {
  label: string;
  path: string;
}

export interface SessionMergeBannerContent {
  type: 'neutral' | 'warning' | 'error';
  message: string;
  links: SessionMergeBannerLink[];
}

interface SessionMergeBannerSource {
  session_id: string;
  status: string;
  metadata?: Record<string, any> | null;
}

export const ARCHIVED_BANNER_MESSAGE =
  'This session was merged into another session and is read only.';
export const MERGING_BANNER_MESSAGE =
  'A merge that includes this session is running. Editing is paused until it finishes.';
export const MERGE_FAILED_BANNER_MESSAGE =
  'A merge that includes this session did not finish. Editing is paused until an admin retries it from Session History.';
export const MERGED_BANNER_MESSAGE = 'This session was created by merging two sessions.';

const sessionIdToPath = (sessionId: string): string => `/session/${sessionId}`;

/**
 * What the detail page tells the viewer about a session's merge, or null when it was never
 * part of one. Archived sources link to their merged session; merged sessions link back to
 * both sources.
 */
export const sessionToMergeBanner = (session: SessionMergeBannerSource): SessionMergeBannerContent | null => {
  if (isSessionArchived(session)) {
    const mergedInto = metadataToMergedInto(session.metadata);
    return {
      type: 'neutral',
      message: ARCHIVED_BANNER_MESSAGE,
      links: mergedInto ? [{ label: 'Open merged session', path: sessionIdToPath(mergedInto) }] : [],
    };
  }

  const record = metadataToMergeRecord(session.metadata);
  if (record?.status === 'merging') {
    return { type: 'warning', message: MERGING_BANNER_MESSAGE, links: [] };
  }
  if (record?.status === 'failed') {
    return { type: 'error', message: MERGE_FAILED_BANNER_MESSAGE, links: [] };
  }

  const mergedFrom = metadataToMergedFrom(session.metadata);
  if (mergedFrom.length > 0) {
    return {
      type: 'neutral',
      message: MERGED_BANNER_MESSAGE,
      links: mergedFrom.map((sourceId, index) => ({
        label: `Open source session ${index + 1}`,
        path: sessionIdToPath(sourceId),
      })),
    };
  }

  return null;
};

export const NO_MERGE_BANNER: SessionMergeBannerContent = { type: 'neutral', message: '', links: [] };

export const LINK_CASE_EMPTY_MESSAGE = 'Enter a Case ID or paste an AUSTIN case URL.';
export const LINK_CASE_INVALID_URL_MESSAGE = 'The AUSTIN case URL is not a valid URL.';
export const LINK_CASE_MISMATCH_MESSAGE = 'The Case ID and the pasted URL refer to different cases.';
export const LINK_CASE_UNRESOLVED_MESSAGE =
  'Could not determine the Case ID from that URL. Please enter the Case ID directly.';

/** What to store for the Link AUSTIN Case inputs, or why they can't be linked. */
export type CaseLink = { error: string } | { error: null; caseId: string; austinLink: string };

/**
 * The Case ID and AUSTIN link the Link AUSTIN Case modal should store, from what the user
 * typed and pasted, or the message explaining why it can't be linked.
 *
 * An entered Case ID wins over the pasted URL, and the two must agree; otherwise the stored
 * case_id and AUSTIN_LINK would point to different cases. A pasted URL is checked first, so a
 * group link is reported as a group number rather than as a mismatch. The pasted URL is kept
 * only when it resolves to the stored Case ID; otherwise the canonical incident link is built.
 */
export const linkInputsToCaseLink = (caseIdInput: string, urlInput: string): CaseLink => {
  const enteredCaseId = normalizeCaseId(caseIdInput);
  const enteredUrl = urlInput.trim();
  if (!enteredCaseId && !enteredUrl) return { error: LINK_CASE_EMPTY_MESSAGE };
  if (enteredUrl && !isValidUrl(enteredUrl)) return { error: LINK_CASE_INVALID_URL_MESSAGE };

  const urlCaseId = enteredUrl ? normalizeCaseId(parseCaseIdFromUrl(enteredUrl) ?? '') : '';
  const urlError = urlCaseId ? caseIdToLinkError(urlCaseId) : null;
  if (urlError) return { error: urlError };
  if (enteredCaseId && urlCaseId && urlCaseId !== enteredCaseId) return { error: LINK_CASE_MISMATCH_MESSAGE };

  const caseId = enteredCaseId || urlCaseId;
  if (!caseId) return { error: LINK_CASE_UNRESOLVED_MESSAGE };
  const caseIdError = caseIdToLinkError(caseId);
  if (caseIdError) return { error: caseIdError };

  const austinLink = enteredUrl && urlCaseId === caseId ? enteredUrl : buildIncidentUrl(caseId);
  return { error: null, caseId, austinLink };
};
